import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Step, StepLabel, Stepper, Tab, Tabs, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import * as cadQuoteApi from '../../lib/api/cadQuote';
import {
  MATERIALS,
  DEFAULT_STOCK_MARGIN_MM,
  DEFAULT_SETUP_MINUTES,
  DEFAULT_HOURLY_RATE_WON,
  DEFAULT_MARGIN_RATE,
  calcStockSize,
  calcAssemblyTotals,
  stripAnalysisForSave,
} from '../../lib/cadQuoteCalc';
import { downloadCadQuoteExcel } from '../../utils/cadQuoteExcel';

import CadQuoteUploadStep from '../../components/cadquote/CadQuoteUploadStep';
import CadQuoteReviewStep from '../../components/cadquote/CadQuoteReviewStep';
import CadQuoteCostStep from '../../components/cadquote/CadQuoteCostStep';
import CadQuotePreviewStep from '../../components/cadquote/CadQuotePreviewStep';
import CadQuoteSimilarPanel from '../../components/cadquote/CadQuoteSimilarPanel';
import CadQuoteSavedQuoteDialog from '../../components/cadquote/CadQuoteSavedQuoteDialog';
import CadQuoteHistoryList from '../../components/cadquote/CadQuoteHistoryList';
import CadQuoteSavePricesDialog from '../../components/cadquote/CadQuoteSavePricesDialog';

const STEPS = ['도면 올리기', '해석 확인·수정', '원가 산출', '견적서 미리보기'];

const EMPTY_TOUCHED = {
  partNo: false,
};

const EMPTY_PART_TOUCHED = {
  name: false,
  quantity: false,
  material: false,
  stockMarginMm: false,
  stockX: false,
  stockY: false,
  stockZ: false,
  purchased: false,
  purchasedUnitPrice: false,
  setupMinutes: false,
};

// 서버에서 못 불러왔을 때 쓰는 임시 기본값(회사 단가표 저장 전과 같은 모양) - 기존 하드코딩
// 값을 그대로 둔다. fetchPrices가 성공하면 이 값들을 서버 값으로 덮어쓴다.
const FALLBACK_SETTING = {
  hourlyRate: DEFAULT_HOURLY_RATE_WON,
  setupMin: DEFAULT_SETUP_MINUTES,
  stockMarginMm: DEFAULT_STOCK_MARGIN_MM,
  marginRate: DEFAULT_MARGIN_RATE,
  updDt: null,
  updNm: null,
};

const emptyCostInputs = (setting) => ({
  hourlyRateWon: setting.hourlyRate,
  marginRate: setting.marginRate,
  outsourceItems: [],
  assemblyMinutes: 0,
});

// 서버가 준 parts[i] 하나(읽기 전용 형상 수치)를 화면에서 고칠 수 있는 part 행으로
// 바꾼다 - 재질 기본값은 그 재질의 density/pricePerKg/roughRate/finishRate를
// part 안에 그대로 복사해둔다(부품마다 따로 고칠 수 있어야 해서 - 과제 요구인
// "재질 테이블은 부품별"을 그대로 따른 것).
// materialsMap/marginMm/setupMinutes는 회사 단가표/공통 기본값(서버에서 불러온 값,
// 못 불러왔으면 하드코딩 기본값)을 그대로 받는다 - MATERIALS를 직접 참조하지 않는다.
const buildPartForm = (serverPart, defaultMaterialCode, materialsMap, marginMm, setupMinutes) => {
  const materialCode = defaultMaterialCode && materialsMap[defaultMaterialCode] ? defaultMaterialCode : 'SKD11';
  const material = materialsMap[materialCode] || MATERIALS.SKD11;
  return {
    id: serverPart.id,
    name: serverPart.name,
    names: serverPart.names || [serverPart.name],
    quantity: serverPart.quantity || 1,
    bbox: serverPart.bbox,
    volumeCm3: serverPart.volumeCm3,
    areaCm2: serverPart.areaCm2,
    minRadius: serverPart.minRadius,
    toolHint: serverPart.toolHint,
    holes: serverPart.holes,
    purchasedGuess: Boolean(serverPart.purchasedGuess),
    purchasedReason: serverPart.purchasedReason,
    previewSvg: serverPart.previewSvg, // 부품별 6면도 - 뷰어의 "6면도" 탭에서 부품을 고르면 이걸 보여준다.
    purchased: Boolean(serverPart.purchasedGuess),
    purchasedUnitPrice: 0,
    material: materialCode,
    density: material.density,
    pricePerKg: material.pricePerKg,
    roughRate: material.roughRate,
    finishRate: material.finishRate,
    stockMarginMm: marginMm,
    stockSize: calcStockSize(serverPart.bbox, marginMm),
    setupMinutes,
    touched: EMPTY_PART_TOUCHED,
  };
};

// 저장(POST /quotes) 전에 part별 previewSvg(6면도 SVG)를 뺀다 - analysisJson과 마찬가지로
// 용량 때문. form.parts는 화면에서만 쓰는 값이라 저장된 견적을 다시 열 때는 치수/재질/
// 원가 입력값만 있으면 된다("단가 가져오기"도 이 필드들만 쓴다).
const stripPartsForSave = (parts) => (parts || []).map(({ previewSvg, ...rest }) => rest);

/**
 * 도면 견적(시범) - 상태는 이 컨테이너 안에 둔다. 2026-10-11부터 "저장된 견적"
 * 탭이 추가돼 DB에도 기록을 남긴다(이전엔 새로고침하면 전부 사라지는 순수 프로토타입이었음).
 *
 * 탭 2개: "새 견적 만들기"(기존 4단계 마법사) / "저장된 견적"(목록 - CadQuoteHistoryList).
 */
const CadQuoteContainer = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [mainTab, setMainTab] = useState(0);

  // ---- 회사 단가표/공통 기본값(서버에서 불러옴, 실패하면 하드코딩 기본값 유지) ----
  const [materials, setMaterials] = useState(MATERIALS);
  const [settingMeta, setSettingMeta] = useState(FALLBACK_SETTING);
  const [pricesLoadFailed, setPricesLoadFailed] = useState(false);
  const [savePricesConfirmOpen, setSavePricesConfirmOpen] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);

  const [activeStep, setActiveStep] = useState(0);

  const [stepFile, setStepFile] = useState(null);
  const [drawingFile, setDrawingFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const [analysis, setAnalysis] = useState(null); // 서버 원본 응답(현재 화면에 보이는 것 - 나눈 뒤면 나눈 결과)
  const [form, setForm] = useState(null); // 사람이 보고 고칠 수 있는 값들(부품별 parts 포함)
  const [touched, setTouched] = useState(EMPTY_TOUCHED);
  const [costInputs, setCostInputs] = useState(() => emptyCostInputs(FALLBACK_SETTING));
  const [selectedPartId, setSelectedPartId] = useState(null); // 2단계: 3D/표에서 고른 부품(전체 보기면 null)
  // "나누기 취소"가 재요청 없이 바로 되돌아가야 해서, 최초(안 나눈) 분석 결과를
  // 따로 보관해둔다 - analysis/form은 나눈 뒤 덮어써지지만 이 둘은 그대로 둔다.
  const [baseAnalysis, setBaseAnalysis] = useState(null);
  const [baseForm, setBaseForm] = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);

  // ---- 저장된 견적(4단계 "견적 저장" + 2단계 "비슷한 과거 견적"에서 쓰는 상세 보기) ----
  const [savedQtUuid, setSavedQtUuid] = useState(null);
  const [saveQuoteLoading, setSaveQuoteLoading] = useState(false);
  const [finalAmtInput, setFinalAmtInput] = useState('');
  const [finalAmtSaving, setFinalAmtSaving] = useState(false);
  const [detailUuid, setDetailUuid] = useState(null); // 비슷한 과거 견적 패널에서 행 클릭 시

  // 회사 단가표/공통 기본값 불러오기 - 페이지 로드 시 한 번, "이 단가를 회사 단가표로
  // 저장" 성공 직후 한 번(서버가 매긴 UPD_DT/UPD_NM을 다시 받아와야 해서) 쓴다.
  const loadPrices = useCallback(async () => {
    try {
      const res = await cadQuoteApi.getCadQuotePrices();
      const data = res?.data || {};
      const list = data.materials || [];
      if (list.length > 0) {
        const map = {};
        list.forEach((m) => {
          map[m.mtrlCd] = {
            mtrlNm: m.mtrlNm,
            density: Number(m.density),
            pricePerKg: Number(m.pricePerKg),
            roughRate: Number(m.roughRate),
            finishRate: Number(m.finishRate),
          };
        });
        setMaterials(map);
      }
      const setting = data.setting;
      if (setting && setting.hourlyRate != null) {
        const nextSetting = {
          hourlyRate: Number(setting.hourlyRate),
          setupMin: Number(setting.setupMin),
          stockMarginMm: Number(setting.stockMarginMm),
          marginRate: Number(setting.marginRate),
          updDt: setting.updDt || null,
          updNm: setting.updNm || null,
        };
        setSettingMeta(nextSetting);
        // 아직 분석 전(파일 업로드 전)이라 사용자가 costInputs를 손댔을 리 없는 시점에만
        // 의미가 있다 - 이 함수가 분석 중간에 다시 불려도(단가표 저장 후) 이미 화면에서
        // 고친 시간당 가공비/마진율까지 덮어쓰진 않는다(저장 버튼 자체가 지금 costInputs를
        // 서버로 보내는 흐름이라 되돌아오는 값도 보통 같다).
        setCostInputs((prev) => ({ ...prev, hourlyRateWon: nextSetting.hourlyRate, marginRate: nextSetting.marginRate }));
      }
      setPricesLoadFailed(false);
    } catch (err) {
      setPricesLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  const handleAnalyze = async () => {
    if (!stepFile) return;
    setLoading(true);
    setProgress(0);
    setErrorMessage('');
    try {
      const res = await cadQuoteApi.analyzeCadFile(stepFile, drawingFile, setProgress);
      const info = cadQuoteApi.extractAnalyzeInfo(res);
      if (!info) {
        throw new Error('분석 결과가 비어 있습니다.');
      }
      setAnalysis(info);

      const initialMaterial = info.titleBlock?.material && materials[info.titleBlock.material]
        ? info.titleBlock.material
        : 'SKD11';
      // 표제란에서 재질을 못 읽으면 원가 기본값(SKD11)과 화면 표시가 어긋나지 않게 SKD11로 가정하고 알린다.
      if (!info.titleBlock?.material) {
        enqueueSnackbar('도면에서 재질을 못 읽어 SKD11로 가정했습니다 - 2단계에서 바꿔주세요.', { variant: 'info' });
      }

      const parts = (info.parts || []).map((part) => buildPartForm(
        part, initialMaterial, materials, settingMeta.stockMarginMm, settingMeta.setupMin,
      ));

      const builtForm = {
        bbox: info.bbox,
        volumeCm3: info.volumeCm3,
        areaCm2: info.areaCm2,
        isAssembly: Boolean(info.isAssembly),
        instanceCount: info.instanceCount || parts.length,
        partNo: info.titleBlock?.partNo || '',
        layered: false,
        splitAt: null,
        splitAxis: null,
        parts,
      };
      setForm(builtForm);
      // "나누기 취소"가 되돌아갈 기준점 - 레이어로 나누기 전엔 현재 결과가 곧 기준점이다.
      setBaseAnalysis(info);
      setBaseForm(builtForm);
      setTouched(EMPTY_TOUCHED);
      setSelectedPartId(null);
      setSavedQtUuid(null);
      setFinalAmtInput('');
      setActiveStep(1);

      (info.warnings || []).forEach((warning) => enqueueSnackbar(warning, { variant: 'warning' }));
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || '분석에 실패했습니다.';
      setErrorMessage(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 레이어로 나누기 - 1단계에서 올린 같은 파일을 splitAt/splitAxis와 함께 다시
  // 분석 서버로 보낸다(파일을 다시 고를 필요 없음, stepFile을 그대로 재사용).
  const handleSplit = async (levels, axis) => {
    if (!stepFile || !levels || !levels.length) return;
    setSplitLoading(true);
    try {
      const res = await cadQuoteApi.analyzeCadFile(stepFile, drawingFile, undefined, { splitAt: levels, splitAxis: axis });
      const info = cadQuoteApi.extractAnalyzeInfo(res);
      if (!info) {
        throw new Error('분석 결과가 비어 있습니다.');
      }
      setAnalysis(info);
      // 나누기 전 부품이 고른 재질(있다면)을 이어서 쓴다 - 안 나눈 상태의 재질 선택을
      // 그냥 버리면 매번 SKD11로 되돌아가 버린다.
      const continuedMaterial = form?.parts?.[0]?.material || 'SKD11';
      const parts = (info.parts || []).map((part) => buildPartForm(
        part, continuedMaterial, materials, settingMeta.stockMarginMm, settingMeta.setupMin,
      ));
      setForm((prev) => ({
        ...prev,
        bbox: info.bbox,
        volumeCm3: info.volumeCm3,
        areaCm2: info.areaCm2,
        isAssembly: Boolean(info.isAssembly),
        instanceCount: info.instanceCount || parts.length,
        layered: Boolean(info.layered),
        splitAt: info.splitAt,
        splitAxis: info.splitAxis,
        parts,
      }));
      setSelectedPartId(null);
      (info.warnings || []).forEach((warning) => enqueueSnackbar(warning, { variant: 'warning' }));
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || '레이어 분할에 실패했습니다.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSplitLoading(false);
    }
  };

  // 나누기 취소 - 재요청 없이 최초(안 나눈) 분석 결과로 그냥 되돌린다.
  const handleCancelSplit = () => {
    if (!baseAnalysis || !baseForm) return;
    setAnalysis(baseAnalysis);
    setForm(baseForm);
    setSelectedPartId(null);
  };

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  // 부품 하나(partId)의 필드 하나를 고친다 - touched도 그 부품 안에서만 따로 켠다.
  const updatePart = (partId, updater) => {
    setForm((prev) => ({
      ...prev,
      parts: prev.parts.map((part) => (part.id === partId ? updater(part) : part)),
    }));
  };

  const handlePartFieldChange = (partId, key, value) => {
    updatePart(partId, (part) => ({
      ...part,
      [key]: key === 'purchased' ? Boolean(value) : value,
      touched: { ...part.touched, [key]: true },
    }));
  };

  // 재질을 바꾸면 그 재질의 기본 단가/가공속도로 다시 채운다 - "재질별 기본값"이라는
  // 과제 요구를 그대로 따른 것. 부품마다 따로 적용된다(조립품은 부품별 재질이 다를 수 있어서).
  // materials는 회사 단가표(서버) - 하드코딩 MATERIALS를 직접 참조하지 않는다.
  const handlePartMaterialChange = (partId, materialCode) => {
    const m = materials[materialCode];
    updatePart(partId, (part) => ({
      ...part,
      material: materialCode,
      density: m ? m.density : part.density,
      pricePerKg: m ? m.pricePerKg : part.pricePerKg,
      roughRate: m ? m.roughRate : part.roughRate,
      finishRate: m ? m.finishRate : part.finishRate,
      touched: { ...part.touched, material: true },
    }));
  };

  // 소재 여유를 바꾸면, 사람이 아직 직접 고치지 않은 축만 공식(bbox+여유*2)대로 다시 맞춘다.
  // 이미 직접 고친 축은 "그 사람이 고른 값"이라 건드리지 않는다.
  const handlePartMarginChange = (partId, value) => {
    updatePart(partId, (part) => {
      const recalculated = calcStockSize(part.bbox, Number(value) || 0);
      const nextStockSize = { ...part.stockSize };
      ['x', 'y', 'z'].forEach((axis) => {
        const touchedKey = `stock${axis.toUpperCase()}`;
        if (!part.touched[touchedKey]) {
          nextStockSize[axis] = recalculated[axis];
        }
      });
      return {
        ...part,
        stockMarginMm: value,
        stockSize: nextStockSize,
        touched: { ...part.touched, stockMarginMm: true },
      };
    });
  };

  const handlePartStockAxisChange = (partId, axis, value) => {
    updatePart(partId, (part) => ({
      ...part,
      stockSize: { ...part.stockSize, [axis]: value },
      touched: { ...part.touched, [`stock${axis.toUpperCase()}`]: true },
    }));
  };

  const handlePartSetupChange = (partId, value) => {
    updatePart(partId, (part) => ({ ...part, setupMinutes: value, touched: { ...part.touched, setupMinutes: true } }));
  };

  const handleCostInputChange = (key, value) => {
    setCostInputs((prev) => ({ ...prev, [key]: key === 'marginRate' ? value : Number(value) }));
  };

  const handleOutsourceChange = (id, key, value) => {
    setCostInputs((prev) => ({
      ...prev,
      outsourceItems: prev.outsourceItems.map((item) => (
        item.id === id ? { ...item, [key]: key === 'amount' ? Number(value) : value } : item
      )),
    }));
  };

  const handleAddOutsource = () => {
    setCostInputs((prev) => ({
      ...prev,
      outsourceItems: [...prev.outsourceItems, { id: Date.now(), name: '', amount: 0 }],
    }));
  };

  const handleRemoveOutsource = (id) => {
    setCostInputs((prev) => ({
      ...prev,
      outsourceItems: prev.outsourceItems.filter((item) => item.id !== id),
    }));
  };

  const computed = useMemo(() => {
    if (!form) {
      return {
        lines: [], materialCost: 0, machiningCost: 0, purchaseCost: 0, assemblyLaborCost: 0,
        outsourceTotal: 0, subtotal: 0, supplyPrice: 0, vat: 0, total: 0,
      };
    }
    const partMaterials = {};
    form.parts.forEach((part) => {
      partMaterials[part.id] = {
        density: part.density, pricePerKg: part.pricePerKg, roughRate: part.roughRate, finishRate: part.finishRate,
      };
    });
    return calcAssemblyTotals({
      parts: form.parts,
      partMaterials,
      machiningInputs: { hourlyRateWon: costInputs.hourlyRateWon },
      outsourceItems: costInputs.outsourceItems,
      assemblyMinutes: costInputs.assemblyMinutes,
      marginRate: costInputs.marginRate,
    });
  }, [form, costInputs]);

  const handleRestart = () => {
    setActiveStep(0);
    setStepFile(null);
    setDrawingFile(null);
    setAnalysis(null);
    setForm(null);
    setBaseAnalysis(null);
    setBaseForm(null);
    setTouched(EMPTY_TOUCHED);
    setCostInputs(emptyCostInputs(settingMeta));
    setSelectedPartId(null);
    setErrorMessage('');
    setSavedQtUuid(null);
    setFinalAmtInput('');
  };

  const handleDownloadExcel = async () => {
    try {
      await downloadCadQuoteExcel({ form, costInputs, computed });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[cadQuoteExcel] 엑셀 생성 실패', err);
      enqueueSnackbar('엑셀 생성에 실패했습니다.', { variant: 'error' });
    }
  };

  // "이 단가를 회사 단가표로 저장" 다이얼로그에 미리 채워줄 값 - 지금 화면 부품들이
  // 실제로 쓰는(part.material을 통해, "단가 가져오기"로 바뀌었을 수도 있는) 값으로
  // materials(회사 단가표)를 덮어써서 보여준다. 다이얼로그 안에서 사람이 한 번 더 고칠 수
  // 있다(요구사항: "saves the materials actually used and edited").
  const materialsForSaveDialog = useMemo(() => {
    const merged = { ...materials };
    (form?.parts || []).forEach((part) => {
      if (!part.purchased && part.material) {
        merged[part.material] = {
          mtrlNm: merged[part.material]?.mtrlNm || part.material,
          density: part.density,
          pricePerKg: part.pricePerKg,
          roughRate: part.roughRate,
          finishRate: part.finishRate,
        };
      }
    });
    return merged;
  }, [materials, form]);

  // 다이얼로그에서 "저장"을 누르면 호출된다 - materialList/setting은 다이얼로그 안에서
  // 고친(또는 그대로 둔) 값이다. 소재여유/셋업은 이 화면에 "공용" 입력이 따로 없어
  // (부품별로만 있음) 기존 공통값을 그대로 둔다.
  //
  // ⚠️ 중요: 회사 단가표를 고쳐도 "지금 이미 만들어진 부품 행(form.parts)"의
  // density/pricePerKg/... 값은 저절로 안 바뀐다(그 값들은 분석 시점에 재질 기본값을
  // 한 번 복사해둔 것 - buildPartForm). 그래서 여기서 저장 직후 현재 form.parts 중
  // 같은 재질을 쓰는 부품도 같이 갱신해준다 - 안 그러면 "방금 고친 단가로 저장했는데
  // 이번 견적엔 적용이 안 된" 것처럼 보이고, 나중에 이 견적을 "비슷한 과거 견적"에서
  // 다시 꺼내 써도 옛날(수정 전) 값이 그대로 나가게 된다.
  const handleSaveCompanyPrices = async (materialList, editedCommon) => {
    setSavingPrices(true);
    try {
      const setting = {
        hourlyRate: editedCommon.hourlyRate,
        marginRate: editedCommon.marginRate,
        setupMin: settingMeta.setupMin,
        stockMarginMm: settingMeta.stockMarginMm,
      };
      await cadQuoteApi.saveCadQuotePrices(materialList, setting);
      await loadPrices();
      setCostInputs((prev) => ({ ...prev, hourlyRateWon: setting.hourlyRate, marginRate: setting.marginRate }));

      const rateByMaterial = {};
      materialList.forEach((m) => {
        rateByMaterial[m.mtrlCd] = {
          density: m.density, pricePerKg: m.pricePerKg, roughRate: m.roughRate, finishRate: m.finishRate,
        };
      });
      setForm((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          parts: prev.parts.map((part) => {
            const rate = rateByMaterial[part.material];
            if (!rate || part.purchased) return part;
            return { ...part, ...rate };
          }),
        };
      });

      enqueueSnackbar('회사 단가표를 저장했습니다.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('단가표 저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSavingPrices(false);
      setSavePricesConfirmOpen(false);
    }
  };

  // 비슷한 과거 견적에서 "단가 가져오기" - 그 견적의 공통값(시간당 가공비/마진율)과
  // 재질별 단가/가공속도(저장 당시 값)를, 지금 화면에서 같은 재질을 쓰는 부품에 적용한다.
  const handleApplySimilarQuote = async (qtUuid) => {
    try {
      const res = await cadQuoteApi.getCadQuote(qtUuid);
      const detail = res?.data || {};
      const histForm = detail.form || {};
      const histCost = histForm.costInputs || {};
      const rateByMaterial = {};
      (histForm.parts || []).forEach((p) => {
        if (p.material) {
          rateByMaterial[p.material] = {
            density: p.density, pricePerKg: p.pricePerKg, roughRate: p.roughRate, finishRate: p.finishRate,
          };
        }
      });

      if (histCost.hourlyRateWon != null || histCost.marginRate != null) {
        setCostInputs((prev) => ({
          ...prev,
          hourlyRateWon: histCost.hourlyRateWon ?? prev.hourlyRateWon,
          marginRate: histCost.marginRate ?? prev.marginRate,
        }));
      }

      setForm((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          parts: prev.parts.map((part) => {
            const rate = rateByMaterial[part.material];
            if (!rate || part.purchased) return part;
            return {
              ...part,
              density: rate.density,
              pricePerKg: rate.pricePerKg,
              roughRate: rate.roughRate,
              finishRate: rate.finishRate,
              touched: { ...part.touched, material: true },
            };
          }),
        };
      });
      enqueueSnackbar('과거 견적의 단가를 적용했습니다.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('단가 가져오기에 실패했습니다.', { variant: 'error' });
    }
  };

  // 4단계 "견적 저장" - STEP(+DXF) 원본과 지금 화면 상태(분석/수정값/계산값)를 그대로 보낸다.
  // analysis는 mesh/previewSvg를 뺀 뒤 보낸다(백엔드도 한 번 더 걷어내지만, 요청 용량 자체를
  // 줄이려고 프론트에서 먼저 뺀다).
  const handleSaveQuote = async () => {
    if (!stepFile || !form) return;
    setSaveQuoteLoading(true);
    try {
      const payload = {
        partNo: form.partNo,
        analysis: stripAnalysisForSave(analysis),
        form: {
          bbox: form.bbox,
          volumeCm3: form.volumeCm3,
          areaCm2: form.areaCm2,
          isAssembly: form.isAssembly,
          instanceCount: form.instanceCount,
          partNo: form.partNo,
          layered: form.layered,
          splitAt: form.splitAt,
          splitAxis: form.splitAxis,
          parts: stripPartsForSave(form.parts),
          costInputs,
        },
        cost: computed.lines,
        amounts: { supplyAmt: computed.supplyPrice, vatAmt: computed.vat, totalAmt: computed.total },
        memo: '',
      };
      const res = await cadQuoteApi.saveCadQuote(stepFile, drawingFile, payload);
      const qtUuid = res?.data?.qtUuid;
      setSavedQtUuid(qtUuid);
      setFinalAmtInput('');
      enqueueSnackbar('견적을 저장했습니다.', { variant: 'success' });
    } catch (err) {
      const message = err?.response?.data?.message || '견적 저장에 실패했습니다.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSaveQuoteLoading(false);
    }
  };

  const handleSaveFinalAmt = async () => {
    if (!savedQtUuid) return;
    setFinalAmtSaving(true);
    try {
      await cadQuoteApi.patchCadQuote(savedQtUuid, Number(finalAmtInput) || 0, '');
      enqueueSnackbar('실제 견적가를 저장했습니다.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('실제 견적가 저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setFinalAmtSaving(false);
    }
  };

  const canGoNext = activeStep === 0 ? false : activeStep < STEPS.length - 1;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>도면 견적(시범)</Typography>

      {pricesLoadFailed && (
        <Alert severity="warning" sx={{ mb: 2 }}>회사 단가표를 못 불러와 임시 기본값을 씁니다.</Alert>
      )}

      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 2 }}>
        <Tab label="새 견적 만들기" />
        <Tab label="저장된 견적" />
      </Tabs>

      {mainTab === 1 && <CadQuoteHistoryList />}

      {mainTab === 0 && (
        <>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap' }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <CadQuoteUploadStep
              stepFile={stepFile}
              drawingFile={drawingFile}
              onStepFileChange={setStepFile}
              onDrawingFileChange={setDrawingFile}
              onAnalyze={handleAnalyze}
              loading={loading}
              progress={progress}
              errorMessage={errorMessage}
            />
          )}

          {activeStep === 1 && form && (
            <>
              <CadQuoteReviewStep
                analysis={analysis}
                form={form}
                materials={materials}
                touched={touched}
                onFieldChange={handleFieldChange}
                selectedPartId={selectedPartId}
                onSelectPart={setSelectedPartId}
                onPartFieldChange={handlePartFieldChange}
                onPartMaterialChange={handlePartMaterialChange}
                onPartMarginChange={handlePartMarginChange}
                onPartStockAxisChange={handlePartStockAxisChange}
                onSplit={handleSplit}
                onCancelSplit={handleCancelSplit}
                splitLoading={splitLoading}
              />
              <CadQuoteSimilarPanel
                analysis={analysis}
                form={form}
                onApplyQuote={handleApplySimilarQuote}
                onOpenDetail={setDetailUuid}
              />
            </>
          )}

          {activeStep === 2 && form && (
            <CadQuoteCostStep
              form={form}
              costInputs={costInputs}
              computed={computed}
              onCostInputChange={handleCostInputChange}
              onPartSetupChange={handlePartSetupChange}
              onOutsourceChange={handleOutsourceChange}
              onAddOutsource={handleAddOutsource}
              onRemoveOutsource={handleRemoveOutsource}
              settingMeta={settingMeta}
              onRequestSavePrices={() => setSavePricesConfirmOpen(true)}
            />
          )}

          {activeStep === 3 && form && (
            <CadQuotePreviewStep
              form={form}
              costInputs={costInputs}
              computed={computed}
              onDownloadExcel={handleDownloadExcel}
              onRestart={handleRestart}
              savedQtUuid={savedQtUuid}
              saveQuoteLoading={saveQuoteLoading}
              onSaveQuote={handleSaveQuote}
              finalAmtInput={finalAmtInput}
              onFinalAmtInputChange={setFinalAmtInput}
              finalAmtSaving={finalAmtSaving}
              onSaveFinalAmt={handleSaveFinalAmt}
            />
          )}

          {(activeStep === 1 || activeStep === 2) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button onClick={() => setActiveStep((s) => s - 1)}>이전</Button>
              {canGoNext && (
                <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)}>다음</Button>
              )}
            </Box>
          )}
        </>
      )}

      <CadQuoteSavePricesDialog
        open={savePricesConfirmOpen}
        materials={materialsForSaveDialog}
        hourlyRate={costInputs.hourlyRateWon}
        marginRate={costInputs.marginRate}
        saving={savingPrices}
        onCancel={() => setSavePricesConfirmOpen(false)}
        onSave={handleSaveCompanyPrices}
      />

      <CadQuoteSavedQuoteDialog open={Boolean(detailUuid)} qtUuid={detailUuid} onClose={() => setDetailUuid(null)} />
    </Box>
  );
};

export default CadQuoteContainer;
